import { BINARY_CONSTANTS, CORPORATION_ENTITY_TYPES, entityTypes, STATES } from 'common/constants';
import type { CheckboxValues } from 'components/lib/QuestionEngine';

export interface LockedWcToggleOption {
  value: string;
  tooltip: string;
}

export interface WcOwnerStateRule {
  isNjLlcAllInOrAllOut: boolean;
  lockedToggleOption: LockedWcToggleOption | undefined;
  forcedWcExcludedValue: CheckboxValues | undefined;
  stateMessage: string;
}

interface GetWcOwnerStateRuleParameters {
  usState: string | undefined;
  entityType: string | undefined;
}

const CORPORATION_FORCE_INCLUDE_STATE_MESSAGES: {
  [state: string]: { description: string; tooltip: string };
} = {
  [STATES.NJ]: {
    description:
      "All corporation Owners/Officers must be included to Workers' Compensation in the state of NJ. Please verify the classification and payroll.",
    tooltip:
      'All corporation officers must be included in WC, they are not allowed to be exclude in the state of NJ.',
  },
  [STATES.MO]: {
    description:
      "All corporation Owners/Officers must be included to Workers' Compensation in the state of MO. Please verify the classification and payroll.",
    tooltip:
      'All corporation officers must be included in WC, they are not allowed to be exclude in the state of MO.',
  },
};

export const getWcOwnerStateRule = ({
  usState,
  entityType,
}: GetWcOwnerStateRuleParameters): WcOwnerStateRule => {
  // NJ requires LLC members to be all-included or all-excluded from WC - never mixed.
  const isNjLlcAllInOrAllOut = usState === STATES.NJ && entityType === entityTypes.LLC.code;

  // NM requires every Sole Proprietor owner/officer to be force-included in WC - exclusion isn't allowed.
  const isNmSoleProprietorForceInclude =
    usState === STATES.NM && entityType === entityTypes.SOLE_PROPRIETOR.code;

  // MI requires every Sole Proprietor owner to be force-excluded from WC - inclusion isn't allowed.
  const isMiSoleProprietorForceExclude =
    usState === STATES.MI && entityType === entityTypes.SOLE_PROPRIETOR.code;

  // NJ and MO require every Corporation owner/officer to be force-included in WC - exclusion isn't allowed.
  const corporationForceIncludeMessages = CORPORATION_ENTITY_TYPES.includes(entityType as string)
    ? CORPORATION_FORCE_INCLUDE_STATE_MESSAGES[usState as string]
    : undefined;

  const isOwnerForceInclude =
    isNmSoleProprietorForceInclude || Boolean(corporationForceIncludeMessages);

  // The single Yes/No option that's locked (disabled, tooltip-explained, and forced as the
  // value) for every owner on this page. Force-include and force-exclude never both apply to
  // the same submission, since they're gated on disjoint state/entityType combinations.
  const lockedToggleOption: LockedWcToggleOption | undefined = isOwnerForceInclude
    ? {
        value: BINARY_CONSTANTS.YES,
        tooltip:
          corporationForceIncludeMessages?.tooltip ??
          'All sole proprietors must be included in WC, they are not allowed to be excluded in the state of NM.',
      }
    : isMiSoleProprietorForceExclude
      ? {
          value: BINARY_CONSTANTS.NO,
          tooltip: 'All sole proprietors must be excluded from WC in the state of MI.',
        }
      : undefined;

  const stateMessage = isNjLlcAllInOrAllOut
    ? "In the state of NJ, all LLC members must be either all included or all excluded from Workers' Compensation — they cannot be mixed. Changing one member's selection will apply the same to all others."
    : isNmSoleProprietorForceInclude
      ? "All Individual Owners/Officers (Sole Proprietors) must be included to Workers' Compensation in the state of NM. Please verify the classification and payroll."
      : isMiSoleProprietorForceExclude
        ? "All Individual Owners (Sole Proprietors) must be excluded from Workers' Compensation in the state of MI."
        : (corporationForceIncludeMessages?.description ?? '');

  // The locked option is the one disabled - the forced value for every owner is the other one.
  const forcedWcExcludedValue: CheckboxValues | undefined = lockedToggleOption
    ? lockedToggleOption.value === BINARY_CONSTANTS.YES
      ? (BINARY_CONSTANTS.NO as CheckboxValues)
      : (BINARY_CONSTANTS.YES as CheckboxValues)
    : undefined;

  return { isNjLlcAllInOrAllOut, lockedToggleOption, forcedWcExcludedValue, stateMessage };
};
